<template>
  <div :class="$options.name">
    <v-dialog v-model="dialog" width="800" max-width="800">
      <template #activator="{ on }">
        <v-btn :color="color" outlined v-on="on">
          <v-icon left>map</v-icon> {{ label }}
        </v-btn>
      </template>

      <div class="dialog-container">
        <map-component
          :is-add-marker="true"
          @add-marker-on-map="addMarkerOnMap"
          @created="onMapCreated"
        />

        <div v-if="selectedCoords" class="accept-coordinates">
          <div><b>{{ latLonCoordiantes }}</b></div>
          <v-btn color="success" class="ml-2" @click="onCoordsSelected">Выбрать</v-btn>
        </div>
      </div>

      <import-file-dialog
        v-if="isImportDialogOpen"
        :is-dialog-open="isImportDialogOpen"
        @closed="toggleImportDialog"
        @add-import-file="addImportFile"
      />
    </v-dialog>
  </div>
</template>

<script>
import Mapbox from 'mapbox-gl';
import MapComponent from '@/components/map';
import MapboxButtonsControl from '@/utils/map-box/MapboxButtonsControl';
import ImportFileDialog from '@/components/dialogs/ImportFileDialog.vue';
import { mapActions } from 'vuex';
import { point } from '@turf/turf';

export default {
  name: 'select-point-dialog',
  components: { MapComponent, ImportFileDialog },
  props: {
    label: {
      type: String,
      default: 'Указать на карте',
    },
    color: String,
    geometry: Object,
  },

  data: () => ({
    dialog: false,
    selectedCoords: null,
    isImportDialogOpen: false,
  }),

  computed: {
    latLonCoordiantes() {
      if (!this.selectedCoords) return null;

      return `${this.selectedCoords.slice().reverse().join(', ')} (Ш,Д)`;
    },
  },

  watch: {
    dialog() {
      if (!this.dialog) {
        this.resetData();
      } else if (this.instance && this.geometry) {
        this.setGeometry(this.geometry.coordinates, true);
      }
    },
  },

  methods: {
    ...mapActions(['showNotify']),

    async onMapCreated(instance) {
      // eslint-disable-next-line no-param-reassign
      instance.getCanvas().style.cursor = 'crosshair';

      instance.on('click', (event) => this.onMapClick(event, instance));

      const importButton = new MapboxButtonsControl({
        title: 'Импорт геометрии',
        iconName: 'cloud_upload',
        clickHandler: this.toggleImportDialog,
      });
      instance.addControl(importButton, 'top-right');

      this.instance = instance;

      if (this.geometry) {
        this.setGeometry(this.geometry.coordinates, true);
      }
    },

    setGeometry(geometry, zoomToGeometry = false) {
      this.selectedCoords = geometry;

      this.pointMarker = new Mapbox.Marker()
        .setDraggable(true)
        .on('dragend', this.onDragEnd)
        .setLngLat(geometry)
        .addTo(this.instance);

      if (zoomToGeometry) {
        this.instance.flyTo({ center: geometry, zoom: 8 });
      }
    },

    toggleImportDialog() {
      this.isImportDialogOpen = !this.isImportDialogOpen;
    },

    addImportFile(file) {
      try {
        if (file.type === 'FeatureCollection') {
          const { coordinates, type } = file.features[0].geometry;

          if (type === 'Point') this.addMarkerOnMap(coordinates);
          else throw new Error('Неверный тип геометрии');
        } else if (file.type === 'Feature') {
          const { coordinates, type } = file.geometry;

          if (type === 'Point') this.addMarkerOnMap(coordinates);
          else throw new Error('Неверный тип геометрии');
        } else {
          throw new Error('Неверный формат файла');
        }

        this.showNotify({ message: 'Геометрия добавлена' });
        this.toggleImportDialog();
      } catch (error) {
        this.showNotify({ type: 'error', message: error.message });
        console.error(error);
      }
    },

    onDragEnd(event) {
      // eslint-disable-next-line no-underscore-dangle
      this.onMapClick({ lngLat: event.target._lngLat }, this.instance);
    },

    onMapClick(event) {
      if (this.pointMarker) this.pointMarker.remove();
      this.setGeometry(event.lngLat.toArray());
    },

    addMarkerOnMap(coordinates) {
      if (this.pointMarker) this.pointMarker.remove();
      this.setGeometry(coordinates);
      this.instance.flyTo({ center: coordinates, zoom: 12 });
    },

    resetData() {
      this.selectedCoords = null;

      if (this.pointMarker) {
        this.pointMarker.off('dragend', this.onDragEnd);
        this.pointMarker.remove();
      }

      this.instance.off('click');
    },

    onCoordsSelected() {
      const selectedPoint = point(this.selectedCoords).geometry;
      this.dialog = false;
      this.$emit('change', selectedPoint);
    },
  },
};
</script>

<style lang="scss" scoped>

.dialog-container {
  position: relative;
  height: 450px;
  width: 100%;
}

.accept-coordinates {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  bottom: 64px;
  left: 0;
  height: 64px;
  background-color: rgba(0, 0, 0, .3);
  width: 100%;
  color: white;
  padding: 12px;
  z-index: 1000;
}
</style>
